'use client';

import { useMemo } from 'react';

import { useTranslation } from '@/hooks/useTranslation';

import { modelApi, skuApi, type RobotSku } from '../../_lib/api';
import { EnabledBadge, EntityAdminPage } from '../_lib/EntityAdminPage';

export default function SkusAdminPage() {
  const { t } = useTranslation();
  const rm = t.robotManager;

  const columns = useMemo(
    () => [
      { key: 'code' as const, label: 'Code' },
      { key: 'name' as const, label: rm.admin.name },
      {
        key: 'model' as const,
        label: rm.fields.model,
        render: (r: RobotSku) => r.model?.name ?? '-',
      },
      { key: 'variant' as const, label: rm.entities.skus.variant },
      {
        key: 'defaultPrice' as const,
        label: rm.entities.skus.defaultPrice,
        render: (r: RobotSku) => (r.defaultPrice != null ? `$${Number(r.defaultPrice).toFixed(2)}` : '-'),
      },
      {
        key: 'defaultCost' as const,
        label: rm.entities.skus.defaultCost,
        render: (r: RobotSku) => (r.defaultCost != null ? `$${Number(r.defaultCost).toFixed(2)}` : '-'),
      },
      {
        key: '_count' as const,
        label: rm.entities.skus.deviceCount,
        render: (r: RobotSku) => r._count?.robotUnits ?? 0,
      },
      {
        key: 'enabled' as const,
        label: rm.admin.status,
        render: (r: RobotSku) => <EnabledBadge enabled={r.enabled} />,
      },
    ],
    [rm],
  );

  const fields = useMemo(
    () => [
      { name: 'code' as const, label: 'Code', placeholder: '留空自动生成，例：AEGIS-STD', hint: rm.admin.codeAutoGeneratedHint },
      { name: 'name' as const, label: rm.admin.name, required: true },
      {
        name: 'modelId' as const,
        label: rm.entities.skus.parentModel,
        required: true,
        type: 'select' as const,
        loader: async () => {
          const models = await modelApi.list({ enabledOnly: 'true' });
          return models.map((m) => ({ value: m.id, label: `${m.code} - ${m.name}` }));
        },
      },
      { name: 'variant' as const, label: rm.entities.skus.variant, placeholder: '如 Standard / Pro' },
      { name: 'defaultPrice' as const, label: rm.entities.skus.defaultPrice, type: 'number' as const },
      { name: 'defaultCost' as const, label: rm.entities.skus.defaultCost, type: 'number' as const },
      { name: 'description' as const, label: rm.entities.description, type: 'textarea' as const },
      { name: 'enabled' as const, label: rm.admin.enabled, type: 'checkbox' as const },
    ],
    [rm],
  );

  return (
    <EntityAdminPage<RobotSku> excelResource="skus"
      title={rm.entities.skus.title}
      description={rm.entities.skus.description}
      api={skuApi}
      columns={columns}
      fields={fields}
      searchPlaceholder={rm.entities.skus.searchPlaceholder}
    />
  );
}
